import { cloneDeep, isArray, keyBy } from 'lodash';

/**
 * Will create an index object from the given tree
 * @param tree - Array of nodes (with or without children) to search
 * @param options - You can specify:
 *                   - idProp: The property used to key the index (default 'id')
 *                   - childrenProp: The children property (default 'children')
 */
export const createIndexFromTree = <T>(
  tree: T[],
  options: Partial<{ idProp: keyof T; childrenProp: keyof T }> = {}
) => {
  const key = options.idProp || ('id' as keyof T);
  return keyBy(flattenTree(tree, options), key);
};

/**
 * Builds a tree from the given index of tree nodes (useful when constructing a new tree).
 * Note: You will probably want to sort the tree after this operation.
 * @param index - index of tree nodes
 * @param options - You can specify:
 *                   - parentIdProp: The property used to store the parentId in each node (default 'parentId')
 *                   - childrenProp: The children property in each node (default 'children')
 */
export const createTreeFromIndex = <T>(
  index: Record<string, T>,
  options: Partial<{ parentIdProp: keyof T; childrenProp: keyof T }> = {}
) => {
  const childrenKey = options.childrenProp || ('children' as keyof T);
  const parentIdKey = options.parentIdProp || ('parentId' as keyof T);

  const tree: T[] = [];
  const newIndex = cloneDeep(index);

  // Wipe out children array for all nodes
  Object.values(newIndex).forEach(node => {
    (node[childrenKey] as T[]) = [];
  });

  // Now re-create children array for all nodes based upon parentId property
  Object.values(newIndex).forEach(node => {
    const parentId = node[parentIdKey] as string;
    const parent = newIndex[parentId];

    if (parent) {
      (parent[childrenKey] as T[]).push(node);
    } else {
      tree.push(node);
    }
  });

  return tree;
};

/**
 * Returns a new tree, transforming every node in the given tree using the given transformation function.
 * Warning: new (transformed) nodes will be modified, so make sure your transform function returns a new object to avoid corruption.
 * @param tree - Array of nodes (with or without children) to transform
 * @param transformFunc - Function to transform each source node. It should return a new target node object.
 * @param options - Matching options. You can specify:
 *                   - sourceChildrenProp: The children property in the source tree (default 'children')
 *                   - targetChildrenProp: The children property in the target tree (default 'children')
 */
export const filterTree = <T>(
  tree: T[],
  filterFunc: (node: T, level: number, parent?: T) => boolean,
  options: { childrenProp?: keyof T } = {},
  level = 0,
  parentNode?: T
): T[] => {
  const childrenKey = options.childrenProp || ('children' as keyof T);

  return tree
    .filter(node => filterFunc(node, level, parentNode))
    .map(node => {
      const children = isArray(node[childrenKey])
        ? filterTree(node[childrenKey] as T[], filterFunc, options, level + 1, node)
        : node[childrenKey];
      return { ...node, [childrenKey]: children };
    });
};

/**
 * Will find the node in the given node array which has the given criteria
 * @param tree - Array of nodes (with or without children) to search
 * @param checkFunc - Function run on each node to find a match
 * @param options - You can specify:
 *                 - childrenProp: The children property (default 'children')
 */
export const findNode = <T>(
  tree: T[],
  checkFunc: (node: T) => boolean,
  options: Partial<{ childrenProp: keyof T }> = {}
): T | undefined => {
  const childrenKey = options.childrenProp || ('children' as keyof T);

  // Attempt to find node in tree array (base level of array)
  let found = tree.find(checkFunc);
  if (found) {
    return found;
  }

  // If we didnt find the node in the base level, search the children of each node
  for (const node of tree as T[]) {
    if (isArray(node[childrenKey])) {
      found = findNode(node[childrenKey] as T[], checkFunc, options);
      if (found) {
        return found;
      }
    }
  }
};

/**
 * Will return a 1 dimentional (flat) array of all nodes in the given tree
 * @param tree - tree nodes array to flatten
 * @param options - You can specify:
 *                   - childrenProp: The children property (default 'children')
 * @param nodes - Used when calling this function recursively. You should not pass data here.
 */
export const flattenTree = <T>(tree: T[], options: Partial<{ childrenProp: keyof T }> = {}, nodes: T[] = []) => {
  const childrenKey = options.childrenProp || ('children' as keyof T);

  tree.forEach(node => {
    nodes.push(node);
    if (isArray(node[childrenKey])) {
      flattenTree(node[childrenKey] as T[], options, nodes);
    }
  });

  return nodes;
};

/**
 * returns an index of all parents (of all levels) for the given node
 * @param index - Index of nodes
 * @param node - Node to extract the parents from
 * @param options - Matching options. You can specify:
 *                   - idProp: The property used to key the index (default 'id')
 *                   - parentIdProp: The parentId property (default 'parentId')
 */
export const getParents = <T>(
  index: Record<string, T>,
  node: T,
  options: { idProp?: keyof T; parentIdProp?: keyof T } = {}
) => {
  const output: Record<string, T> = {};
  let currentNode: T | undefined = node;

  while (currentNode) {
    const parentId = currentNode[options.parentIdProp || ('parentId' as keyof T)] as string;
    if (parentId && index[parentId]) {
      output[parentId] = index[parentId];
      currentNode = index[parentId];
    } else {
      currentNode = undefined;
    }
  }

  return output;
};

/**
 * Sorts a tree by the given property. Warning: This modifies the input tree.
 * @param tree - Array of nodes (with or without children) to sort
 * @param options - Matching options. You can specify:
 *                   - sortProp: The property used to compare when sorting (default 'name')
 *                   - childrenProp: The children property (default 'children')
 */
export const sortTree = <T>(tree: T[], options: { sortProp?: keyof T; childrenProp?: keyof T } = {}) => {
  const sortKey = options.sortProp || ('name' as keyof T);
  const childrenKey = options.childrenProp || ('children' as keyof T);

  tree.sort((a, b) => {
    if (a[sortKey] < b[sortKey]) {
      return -1;
    }
    if (a[sortKey] > b[sortKey]) {
      return 1;
    }
    return 0;
  });
  tree.forEach(node => {
    const children = node[childrenKey] as T[];
    if (isArray(children) && children.length) {
      sortTree(children, options);
    }
  });
};

/**
 * Returns a new tree, transforming every node in the given tree using the given transformation function.
 * Warning: new (transformed) nodes will be modified, so make sure your transform function returns a new object to avoid corruption.
 * @param tree - Array of nodes (with or without children) to transform
 * @param transformFunc - Function to transform each source node. It should return a new target node object.
 * @param options - Matching options. You can specify:
 *                   - sourceChildrenProp: The children property in the source tree (default 'children')
 *                   - targetChildrenProp: The children property in the target tree (default 'children')
 */
export const transformTree = <T, U>(
  tree: T[],
  transformFunc: (node: T, level: number, parent: T | undefined, parentTree: T[]) => U,
  options: { sourceChildrenProp?: keyof T; targetChildrenProp?: keyof U } = {},
  level = 0,
  parentNode?: T
) => {
  const sourceChildrenKey = options.sourceChildrenProp || ('children' as keyof T);
  const targetChildrenKey = options.targetChildrenProp || ('children' as keyof U);
  const output: U[] = [];

  tree.forEach((node, index) => {
    output[index] = transformFunc(node, level, parentNode, tree);
    const sourceChildren = node[sourceChildrenKey] as T[];
    if (isArray(sourceChildren)) {
      (output[index][targetChildrenKey] as U[]) = transformTree(
        sourceChildren,
        transformFunc,
        options,
        level + 1,
        node
      );
    }
  });

  return output;
};
